import { useState, useContext, useEffect } from "react";
import { Context } from "Context";
import { getCookie, setCookie } from "utils/cookie";
import { getSurveyUrl } from "utils/surveyParams";
import { isCurrentPathPresent, getCdn } from "utils";
import appConfig from "config.json";
import Image from "components/shared/Image";
import Container from "components/shared/Container";

const surveyThresholdCookieName = "surveyThreshold_jar";
const visitThresholdKey = "pageViewThreshold";

const Survey = () => {
	const { search } = window.location;
	const {
		headerData,
		footerData,
		legalFooterData,
		setModal,
		config: { brand },
		profileData,
	} = useContext(Context);

	const [surveyConfig, setSurveyConfig] = useState(null);
	const brandConfig = appConfig.brands?.[brand];
	const surveyCookieName =
		brandConfig?.surveyCookieName || appConfig?.surveyCookieName;
	const customSurveyCookieValue = !!brandConfig?.surveyCookieValue;
	const surveyCookieValue = customSurveyCookieValue
		? brandConfig?.surveyCookieValue
		: appConfig.surveyCookieValue;

	const setPageViewCount = (pageViewCount) => {
		setCookie(
			surveyThresholdCookieName,
			encodeURIComponent(JSON.stringify({ [visitThresholdKey]: pageViewCount }))
		);
	};

	const setSkipSurvey = () => {
		let surveyCookieCurrentValue;
		if (customSurveyCookieValue) {
			surveyCookieCurrentValue = encodeURIComponent(surveyCookieValue);
		} else {
			surveyCookieCurrentValue = encodeURIComponent(
				JSON.stringify({ [surveyCookieValue]: true })
			);
		}
		setCookie(
			surveyCookieName,
			surveyCookieCurrentValue,
			appConfig.cookieExpirationInDays
		);
	};

	const showSurvey = (surveyURL) => {
		const showBrandLogoInSurveyModal = brandConfig?.surveyBrandLogo;
		let bannerImageHtml = null;
		if (showBrandLogoInSurveyModal) {
			const brandLogo = brandConfig?.logo;
			const syndicatedCdn = getCdn();
			const brandLogoSrc = `${syndicatedCdn}/logos/${
				brandLogo || `${brand}.svg`
			}`;
			bannerImageHtml = (
				<Container direction="row-reverse" modifier="survey">
					<Image
						image={{
							url: brandLogoSrc,
							alt: "Survey Logo",
							width: "140",
						}}
						name="survey-logo"
					/>
				</Container>
			);
		}
		setModal({
			...surveyConfig,
			href: surveyURL,
			target: "_blank",
			logo: bannerImageHtml,
			customClassModifier: "survey",
		});
	};

	useEffect(() => {
		const config =
			headerData?.modalSurvey ||
			footerData?.modalSurvey ||
			legalFooterData?.modalSurvey;
		if (!surveyConfig && config) {
			setSurveyConfig(config);
		}
	}, [surveyConfig, headerData, footerData, legalFooterData]);

	useEffect(() => {
		if (
			!surveyConfig ||
			!surveyConfig.allowedPages ||
			!surveyConfig[visitThresholdKey] ||
			!surveyConfig.surveyUrls ||
			!surveyConfig.percentageToShow
		) {
			return;
		}

		const surveyURL = getSurveyUrl(
			surveyConfig.surveyUrls,
			profileData?.user?.swid
		);

		if (search.includes("showSurvey")) {
			showSurvey(surveyURL);
			return;
		}

		let skipSurvey = false;

		if (customSurveyCookieValue) {
			skipSurvey = getCookie(surveyCookieName);
		} else {
			skipSurvey = getCookie(surveyCookieName, true)?.[surveyCookieValue];
		}

		if (skipSurvey) return;

		const surveyThreshold =
			getCookie(surveyThresholdCookieName, true)?.[visitThresholdKey] || 0;
		const currentIsExcludedPage = isCurrentPathPresent(
			surveyConfig.excludedPages
		);
		const currentIsAllowedPage =
			!currentIsExcludedPage && isCurrentPathPresent(surveyConfig.allowedPages);
		if (surveyThreshold < surveyConfig[visitThresholdKey]) {
			setPageViewCount(surveyThreshold + 1);
		} else if (currentIsAllowedPage) {
			const randomPercentage = Math.floor(Math.random() * 100 + 1);
			if (randomPercentage <= surveyConfig.percentageToShow) {
				showSurvey(surveyURL);
			}
			setSkipSurvey();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [surveyConfig]);

	return null;
};

export default Survey;
